import React, { useState, useRef, useEffect } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CloudArrowUp, CheckCircle, Warning, XCircle, Question, Table, Code } from "@phosphor-icons/react";
import { toast } from "sonner";

const SAMPLE_JSON = `[
  {
    "id": "evt_001",
    "company": "MYMRCH_CO",
    "data": {
      "id": "myze_order_99281",
      "externalOrderNumber": "MYM-2026-1042",
      "shopId": "77199638860",
      "items": [{"baseProduct": "tshirt-classic", "quantity": 1, "printFront": true, "printBack": true}],
      "shippingAddress": {"country": "IT"}
    }
  }
]`;

export default function AdminMyzeImport() {
  const { t } = useI18n();
  const [mode, setMode] = useState("csv");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    if (result && bannerRef.current) {
      bannerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const submit = async () => {
    if (!text.trim()) {
      toast.error("Incolla un payload prima di importare");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      let data;
      if (mode === "csv") {
        const r = await api.post("/admin/myze-import-csv", { csv: text });
        data = r.data;
      } else {
        let parsed;
        try { parsed = JSON.parse(text.trim()); }
        catch { toast.error("JSON non valido"); setLoading(false); return; }
        const payloads = Array.isArray(parsed) ? parsed : [parsed];
        const r = await api.post("/admin/myze-import", { payloads });
        data = r.data;
      }
      setResult(data);
      toast.success(`Import: ${data.imported} nuovi · ${data.duplicates} duplicati · ${data.blocked} bloccati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante import");
    } finally {
      setLoading(false);
    }
  };

  const onFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await loadFile(f);
  };

  const loadFile = async (f) => {
    const t = await f.text();
    setText(t);
    setMode(f.name.toLowerCase().endsWith(".json") ? "json" : "csv");
    toast.success(`File caricato: ${f.name}`);
  };

  const [dragOver, setDragOver] = useState(false);
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    const isCsv = f.name.toLowerCase().endsWith(".csv") || f.type === "text/csv";
    const isJson = f.name.toLowerCase().endsWith(".json") || f.type === "application/json";
    if (!isCsv && !isJson) {
      toast.error("Trascina un file .csv o .json");
      return;
    }
    await loadFile(f);
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-myze-import">
      <div>
        <div className="tf-label">RECUPERO ORDINI</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-1">Importa ordini da Myze</h1>
        <p className="text-sm text-[var(--tf-muted)] mt-2">
          Carica il file <strong>CSV</strong> esportato da Myze (Orders → Export) o incolla JSON.
          Gli ordini già presenti vengono <strong>ignorati automaticamente</strong>.
        </p>
      </div>

      <div className="tf-card p-5 bg-[var(--tf-bg)] border-l-4 border-[var(--tf-ink)]">
        <div className="flex items-start gap-3">
          <Question size={20} weight="bold" className="text-[var(--tf-ink)] mt-0.5" />
          <div className="text-sm">
            <div className="font-display font-bold">Come ottenere i dati da Myze</div>
            <ol className="text-[var(--tf-muted)] mt-2 space-y-1 list-decimal list-inside">
              <li>Apri Myze → <strong>Orders</strong></li>
              <li>Filtra per data (es. ultime 24h o 7 giorni)</li>
              <li>Click su <strong>Export</strong> → scarica file <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded">.csv</code></li>
              <li>Carica il file qui sotto o trascinalo nella textarea</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Mode tabs + drop zone wrapper */}
      <div
        className={`tf-card p-4 relative transition-all ${dragOver ? "ring-4 ring-[var(--tf-primary)] ring-offset-2" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="drop-zone"
      >
        {dragOver && (
          <div className="absolute inset-0 z-10 bg-[var(--tf-primary)]/15 border-4 border-dashed border-[var(--tf-primary)] rounded-2xl flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <CloudArrowUp size={56} weight="duotone" className="mx-auto text-[var(--tf-ink)]" />
              <div className="font-display font-black text-2xl mt-2 text-[var(--tf-ink)]">Rilascia il file qui</div>
              <div className="text-sm text-[var(--tf-muted)] mt-1">CSV o JSON</div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 mb-4">
          <button onClick={() => setMode("csv")} className={`tf-btn ${mode === "csv" ? "tf-btn-primary" : "tf-btn-ghost"}`} data-testid="mode-csv">
            <Table size={14} weight="bold" /> CSV (consigliato)
          </button>
          <button onClick={() => setMode("json")} className={`tf-btn ${mode === "json" ? "tf-btn-primary" : "tf-btn-ghost"}`} data-testid="mode-json">
            <Code size={14} weight="bold" /> JSON
          </button>
          <label className="tf-btn tf-btn-ghost cursor-pointer" data-testid="file-upload">
            <CloudArrowUp size={14} weight="bold" />
            Carica file .csv / .json
            <input type="file" accept=".csv,.json,text/csv,application/json" onChange={onFileSelect} className="hidden" />
          </label>
          <button
            onClick={() => setText(mode === "json" ? SAMPLE_JSON : "id,location_id,company_id,order_id,created_at_UTC,origin_id,origin_place,customer,print_faces,product_name,product_colour,product_size,product_sku,product_category,storage_place,store_id,shipping_carrier,shipping_tracking_number,shipping_country,shipping_date,status\n")}
            className="text-xs text-[var(--tf-muted)] hover:text-[var(--tf-ink)] underline ml-auto self-center"
          >
            Carica esempio
          </button>
        </div>

        <div className="text-xs text-[var(--tf-muted)] mb-2 flex items-center gap-1.5">
          <CloudArrowUp size={14} weight="bold" />
          Trascina qui un file .csv / .json oppure incollalo nel box sottostante
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={mode === "csv" ? 10 : 14}
          placeholder={mode === "csv" ? "Incolla qui il contenuto del file CSV scaricato da Myze...\nO trascina/carica il file qui sopra" : "[{...}, {...}]"}
          className="tf-input font-mono text-xs"
          spellCheck={false}
          data-testid="import-textarea"
        />
        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-[var(--tf-muted)]">
            {text ? `${text.length} caratteri · ~${text.split("\n").length - 1} righe` : "Nessun contenuto"}
          </div>
          <button onClick={submit} disabled={loading || !text.trim()} className="tf-btn tf-btn-primary" data-testid="import-submit">
            <CloudArrowUp size={16} weight="bold" />
            {loading ? "Import in corso..." : "Importa ordini"}
          </button>
        </div>
      </div>

      {result && (
        <ImportBanner ref={bannerRef} result={result} onClear={() => setResult(null)} />
      )}

      {result && (
        <div className="tf-card p-6" data-testid="import-result">
          <div className="font-display font-bold text-xl mb-4">Risultato import</div>
          {result.parsed_orders !== undefined && (
            <div className="text-sm text-[var(--tf-muted)] mb-3">
              {result.parsed_orders} ordini analizzati dal CSV
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Stat icon={CheckCircle} label="Importati" value={result.imported} color="var(--tf-success)" />
            <Stat icon={Warning} label="Bloccati" value={result.blocked} color="#c47800" />
            <Stat icon={Question} label="Non abbinati" value={result.unmatched} color="var(--tf-muted)" />
            <Stat icon={XCircle} label="Duplicati" value={result.duplicates} color="var(--tf-muted)" />
            <Stat icon={XCircle} label="Errori" value={result.errors} color="var(--tf-error)" />
          </div>

          <div className="tf-label mb-2">Dettaglio</div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto scroll-thin">
            {result.details.map((d, i) => {
              const status = d.duplicate ? "duplicate" : d.blocked ? "blocked" : d.matched === false ? "unmatched" : d.error ? "error" : d.charged ? "charged" : "ok";
              const colorMap = { charged: "tf-status-success", blocked: "tf-status-error", error: "tf-status-error", duplicate: "tf-status-neutral", unmatched: "tf-status-warning", ok: "tf-status-info" };
              return (
                <div key={i} className="p-3 bg-[var(--tf-bg)] rounded-lg text-sm" data-testid={`result-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-xs text-[var(--tf-muted)]">#{i + 1}</span>
                    {d.order_number && <span className="font-mono text-xs font-bold">{d.order_number}</span>}
                    <span className={`tf-status-pill ${colorMap[status]}`}>{status}</span>
                    <span className="text-xs text-[var(--tf-muted)] flex-1 text-right truncate">
                      {d.error || d.reason || (d.charged ? `addebitato · saldo ${d.balance_after}€` : d.order_id?.slice(0, 12))}
                    </span>
                  </div>
                  {d.unmapped_items && d.unmapped_items.length > 0 && (
                    <div className="mt-2 pl-6 text-xs text-[#8a6a00]">
                      ⚠️ Prodotti non mappati (non addebitati): {d.unmapped_items.map((u) => u.product_name).join(", ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="tf-card p-3 text-center">
      <Icon size={24} weight="duotone" style={{ color }} className="mx-auto" />
      <div className="font-display font-black text-2xl tf-numeric mt-1" style={{ color }}>{value}</div>
      <div className="tf-label">{label}</div>
    </div>
  );
}

const ImportBanner = React.forwardRef(function ImportBanner({ result, onClear }, ref) {
  const imported = result.imported || 0;
  const duplicates = result.duplicates || 0;
  const blocked = result.blocked || 0;
  const errors = result.errors || 0;
  const unmatched = result.unmatched || 0;
  const totalCharged = (result.details || [])
    .filter((d) => d.charged && d.balance_after != null)
    .reduce((acc, d) => {
      const prev = acc.lastBal;
      const cur = d.balance_after;
      acc.lastBal = cur;
      return acc;
    }, { lastBal: null });

  const unmappedCount = (result.details || []).reduce(
    (n, d) => n + (d.unmapped_items?.length || 0),
    0,
  );

  // Tone: success (everything fine), warning (some blocked/unmatched/unmapped), error (errors > 0)
  const tone = errors > 0 ? "error"
              : (blocked > 0 || unmatched > 0 || unmappedCount > 0) ? "warning"
              : imported > 0 ? "success"
              : "neutral";

  const toneStyles = {
    success: { bg: "rgba(204,242,44,0.18)", border: "var(--tf-primary)", ink: "var(--tf-ink)", Icon: CheckCircle },
    warning: { bg: "rgba(255,193,7,0.12)", border: "#e0a800", ink: "#8a6a00", Icon: Warning },
    error:   { bg: "rgba(220,53,69,0.10)", border: "var(--tf-error)", ink: "var(--tf-error)", Icon: XCircle },
    neutral: { bg: "var(--tf-bg)", border: "var(--tf-border)", ink: "var(--tf-muted)", Icon: Question },
  }[tone];
  const Icon = toneStyles.Icon;

  // Build the inline summary text
  const parts = [];
  if (imported > 0) parts.push(`${imported} nuov${imported === 1 ? "o" : "i"}`);
  if (duplicates > 0) parts.push(`${duplicates} duplicat${duplicates === 1 ? "o" : "i"} saltat${duplicates === 1 ? "o" : "i"}`);
  if (blocked > 0) parts.push(`${blocked} bloccat${blocked === 1 ? "o" : "i"} (saldo insufficiente)`);
  if (unmatched > 0) parts.push(`${unmatched} non abbinat${unmatched === 1 ? "o" : "i"}`);
  if (errors > 0) parts.push(`${errors} error${errors === 1 ? "e" : "i"}`);
  const summary = parts.length ? parts.join(" · ") : "Nessun ordine processato";

  const title = errors > 0 ? "Import completato con errori"
              : imported === 0 && duplicates > 0 ? "Nessun nuovo ordine — tutti già presenti"
              : imported > 0 ? "Import completato"
              : "Nessun ordine importato";

  return (
    <div
      ref={ref}
      className="tf-card p-5 border-l-4 transition-all"
      style={{ background: toneStyles.bg, borderLeftColor: toneStyles.border }}
      data-testid="import-banner"
    >
      <div className="flex items-start gap-4">
        <Icon size={36} weight="duotone" style={{ color: toneStyles.border }} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-xl" style={{ color: toneStyles.ink }} data-testid="banner-title">
            {title}
          </div>
          <div className="text-sm mt-1.5 font-medium" style={{ color: toneStyles.ink }} data-testid="banner-summary">
            {summary}
          </div>
          {result.parsed_orders !== undefined && (
            <div className="text-xs text-[var(--tf-muted)] mt-1">
              {result.parsed_orders} ordini totali analizzati nel CSV
            </div>
          )}
          {unmappedCount > 0 && (
            <div className="mt-3 text-xs flex items-start gap-2 px-3 py-2 rounded bg-white/60 border border-[#e0a800]/30">
              <Warning size={14} weight="bold" className="shrink-0 mt-0.5 text-[#8a6a00]" />
              <span className="text-[#8a6a00]">
                <b>{unmappedCount} articol{unmappedCount === 1 ? "o" : "i"} non mappat{unmappedCount === 1 ? "o" : "i"}</b> sul listino prezzi —
                addebitato il picking di magazzino come premade. Vedi il dettaglio sotto.
              </span>
            </div>
          )}
        </div>
        <button
          onClick={onClear}
          className="text-xs text-[var(--tf-muted)] hover:text-[var(--tf-ink)] underline shrink-0"
          data-testid="banner-dismiss"
        >
          Chiudi
        </button>
      </div>
    </div>
  );
});
